
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { RoyaltyReport } from '@/types';
import { formatCurrency } from '@/utils/formatters';
import { normalizeCountryCode } from '@/utils/countries';

interface BooksBreakdownTableProps {
  report: RoyaltyReport;
  currentQuarterReports: RoyaltyReport[];
  totalSales: number;
  totalGrossSales: number;
  totalReturns: number;
  totalNetSales: number;
  totalRoyaltyAmount: number;
}

const BooksBreakdownTable: React.FC<BooksBreakdownTableProps> = ({
  report,
  currentQuarterReports,
  totalSales,
  totalGrossSales,
  totalNetSales,
  totalRoyaltyAmount
}) => {
  // Filter to only show reports for the exact quarter and year
  const filteredReports = currentQuarterReports.filter(r => 
    r.quarter === report.quarter && r.year === report.year
  );

  // Calculate previous royalty income (using netSales field - allow 0 values)
  const calculatePreviousRoyaltyIncome = (bookReport: RoyaltyReport) => {
    return bookReport.netSales ?? 0; // Use nullish coalescing to allow 0 values
  };

  const totalPreviousRoyaltyIncome = filteredReports.length > 0 
    ? filteredReports.reduce((sum, r) => sum + calculatePreviousRoyaltyIncome(r), 0)
    : calculatePreviousRoyaltyIncome(report);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Books Breakdown for Q{report.quarter} {report.year}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <ScrollArea className="w-full">
          <div className="min-w-[800px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[200px]">Book Title</TableHead>
                  <TableHead className="min-w-[80px]">Format</TableHead>
                  <TableHead className="min-w-[80px]">Country</TableHead>
                  <TableHead className="min-w-[80px]">Quarter</TableHead>
                  <TableHead className="text-right min-w-[100px]">Units Sold</TableHead>
                  <TableHead className="text-right min-w-[120px]">Gross Sales</TableHead>
                  <TableHead className="text-right min-w-[140px]">Previous Royalty Income</TableHead>
                  <TableHead className="text-right min-w-[140px]">Total Royalty Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredReports.length > 0 ? (
                  filteredReports.map((bookReport) => (
                    <TableRow key={bookReport.id}>
                      <TableCell className="font-medium">
                        <div className="truncate max-w-[180px]" title={bookReport.books?.title || 'Unknown Book'}>
                          {bookReport.books?.title || 'Unknown Book'}
                        </div>
                      </TableCell>
                      <TableCell>
                        {(bookReport.bookFormat || 'paperback').charAt(0).toUpperCase() + (bookReport.bookFormat || 'paperback').slice(1)}
                      </TableCell>
                      <TableCell>
                        {normalizeCountryCode(bookReport.country || 'US')}
                      </TableCell>
                      <TableCell className="font-semibold">
                        Q{bookReport.quarter} {bookReport.year}
                      </TableCell>
                      <TableCell className="text-right">{bookReport.unitsSold || bookReport.salesCount}</TableCell>
                      <TableCell className="text-right">{formatCurrency(bookReport.grossSales || bookReport.amount)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(calculatePreviousRoyaltyIncome(bookReport))}</TableCell>
                      <TableCell className="text-right">{formatCurrency(bookReport.royaltyAmount || bookReport.amount)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  // Fallback for single book report (legacy data) - only if it matches the quarter/year
                  report.quarter === report.quarter && report.year === report.year ? (
                    <TableRow>
                      <TableCell className="font-medium">
                        <div className="truncate max-w-[180px]" title={report.books?.title || 'Unknown Book'}>
                          {report.books?.title || 'Unknown Book'}
                        </div>
                      </TableCell>
                      <TableCell>
                        {(report.bookFormat || 'paperback').charAt(0).toUpperCase() + (report.bookFormat || 'paperback').slice(1)}
                      </TableCell>
                      <TableCell>
                        {normalizeCountryCode(report.country || 'US')}
                      </TableCell>
                      <TableCell className="font-semibold">
                        Q{report.quarter} {report.year}
                      </TableCell>
                      <TableCell className="text-right">{report.unitsSold || report.salesCount}</TableCell>
                      <TableCell className="text-right">{formatCurrency(report.grossSales || report.amount)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(calculatePreviousRoyaltyIncome(report))}</TableCell>
                      <TableCell className="text-right">{formatCurrency(report.royaltyAmount || report.amount)}</TableCell>
                    </TableRow>
                  ) : null
                )}
                
                <TableRow className="font-medium bg-muted/30">
                  <TableCell>Total</TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell className="text-right">{totalSales.toLocaleString()}</TableCell>
                  <TableCell className="text-right">{formatCurrency(totalGrossSales)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(totalPreviousRoyaltyIncome)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(totalRoyaltyAmount)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default BooksBreakdownTable;
